/**
 * Yup validation schemas for Expense Invoice forms
 */

import * as yup from 'yup';
import { ExpenseInvoiceStatus } from '../Types/expenseInvoice.types';

/**
 * Validation schema for expense invoice item
 */
export const expenseInvoiceItemSchema = yup.object({
  name: yup
    .string()
    .required('Nazwa pozycji jest wymagana')
    .max(256, 'Nazwa nie może przekraczać 256 znaków'),
  quantity: yup
    .number()
    .required('Ilość jest wymagana')
    .min(0.01, 'Ilość musi być większa niż 0')
    .typeError('Ilość musi być liczbą'),
  unitNet: yup
    .number()
    .required('Cena netto jest wymagana')
    .typeError('Cena netto musi być liczbą'),
  unitGross: yup
    .number()
    .required('Cena brutto jest wymagana')
    .typeError('Cena brutto musi być liczbą'),
  vatRate: yup
    .number()
    .required('Stawka VAT jest wymagana')
    .min(0, 'Stawka VAT nie może być ujemna')
    .max(1, 'Stawka VAT nie może przekraczać 100%')
    .typeError('Stawka VAT musi być liczbą'),
  isVatExempt: yup.boolean().default(false),
  vatExemptNote: yup
    .string()
    .nullable()
    .when('isVatExempt', {
      is: true,
      then: (schema) => schema.required('Nota zwolnienia jest wymagana dla zwolnienia z VAT'),
      otherwise: (schema) => schema.nullable(),
    }),
});

/**
 * Validation schema for create/update expense invoice
 */
export const expenseInvoiceSchema = yup.object({
  // Basic info
  invoiceNumber: yup
    .string()
    .required('Numer faktury jest wymagany')
    .max(50, 'Numer faktury nie może przekraczać 50 znaków'),
  issueDate: yup
    .string()
    .required('Data wystawienia jest wymagana')
    .test('not-future', 'Data wystawienia nie może być w przyszłości', (value) => {
      if (!value) return true;
      return new Date(value) <= new Date();
    }),
  saleDate: yup
    .string()
    .nullable()
    .notRequired()
    .test('not-future', 'Data sprzedaży nie może być w przyszłości', (value) => {
      if (!value) return true;
      return new Date(value) <= new Date();
    }),
  paymentDueDate: yup
    .string()
    .nullable()
    .notRequired()
    .test(
      'after-issue',
      'Termin płatności nie może być wcześniejszy niż data wystawienia',
      function (value) {
        const { issueDate } = this.parent;
        if (!value || !issueDate) return true;
        return new Date(value) >= new Date(issueDate);
      }
    ),

  // Supplier info
  supplierName: yup
    .string()
    .required('Nazwa dostawcy jest wymagana')
    .max(256, 'Nazwa dostawcy nie może przekraczać 256 znaków'),
  // Strict Polish NIP format (10 digits) only applies to a domestic (PLN) invoice. A non-PLN
  // currency means a foreign supplier whose tax identifier follows a country-specific format
  // (e.g. Hungarian "HU26071303"), matching the backend exemption in
  // CreateExpenseInvoiceCommandValidator / UpdateExpenseInvoiceCommandValidator.
  supplierNip: yup
    .string()
    .nullable()
    .notRequired()
    .when('currency', {
      is: (currency: string | undefined) => !currency || currency === 'PLN',
      then: (schema) =>
        schema
          .matches(/^[0-9]{10}$/, 'NIP musi składać się z 10 cyfr')
          .test('valid-nip', 'Nieprawidłowy numer NIP', (value) => {
            if (!value) return true;
            return /^[0-9]{10}$/.test(value);
          }),
    }),
  supplierAddress: yup
    .string()
    .nullable()
    .notRequired()
    .max(500, 'Adres nie może przekraczać 500 znaków'),
  supplierBankAccount: yup
    .string()
    .nullable()
    .notRequired()
    .max(34, 'Numer konta nie może przekraczać 34 znaków (format IBAN)'),

  // Currency
  currency: yup
    .string()
    .required('Waluta jest wymagana')
    .oneOf(['PLN', 'EUR', 'USD', 'GBP', 'CHF'], 'Nieprawidłowa waluta'),

  // Company ID (optional for manual creation)
  companyId: yup.number().nullable().notRequired(),

  // Status (optional for manual creation, defaults to Draft)
  status: yup
    .string()
    .nullable()
    .notRequired()
    .oneOf(['draft', 'received', 'verified', 'paid', 'cancelled'], 'Nieprawidłowy status'),

  // Notes
  notes: yup
    .string()
    .nullable()
    .notRequired()
    .max(1000, 'Notatki nie mogą przekraczać 1000 znaków'),

  // Items
  items: yup
    .array()
    .of(expenseInvoiceItemSchema)
    .min(1, 'Dodaj przynajmniej jedną pozycję faktury')
    .required('Pozycje faktury są wymagane'),
});

/**
 * Manual type definitions to ensure compatibility with React Hook Form
 * Yup's InferType generates required fields even for nullable values,
 * which conflicts with React Hook Form's optional field expectations.
 */
export interface ExpenseInvoiceItemFormData {
  name: string;
  quantity: number;
  unitNet: number;
  unitGross: number;
  vatRate: number;
  isVatExempt: boolean;
  vatExemptNote?: string | null;
}

export interface ExpenseInvoiceFormData {
  invoiceNumber: string;
  issueDate: string;
  saleDate?: string | null;
  paymentDueDate?: string | null;
  supplierName: string;
  supplierNip?: string | null;
  supplierAddress?: string | null;
  supplierBankAccount?: string | null;
  companyId?: number | null;
  currency: string;
  status?: ExpenseInvoiceStatus;
  notes?: string | null;
  items: ExpenseInvoiceItemFormData[];
}
